import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { AddToWatchlistIcon } from '@sorare/core/src/atoms/icons/AddToWatchlistIcon';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Breadcrumb } from '@sorare/core/src/components/breadcrumb';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { NewHeader } from '@sorare/core/src/components/seo/NewHeader';
import {
  ANY_SPORT_HOME,
  ANY_SPORT_PLAYERS_SLUG,
  ANY_SPORT_SCOUTING_CENTER,
  ANY_SPORT_TEAMS_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useIntlCountries } from '@sorare/core/src/hooks/useIntlCountries';
import { glossary } from '@sorare/core/src/lib/glossary';
import { positionShortNames } from '@sorare/core/src/lib/players';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { LastOwnedCardsByPlayer } from '@sorare/marketplace/src/components/player/LastOwnedCardsByPlayer';
import { AddToWatchlistButton } from '@sorare/marketplace/src/components/watchlists/AddToWatchlistButton';

import { AnyPlayerHeaderRow } from './AnyPlayerHeaderRow/index.tsx';
import type { AnyPlayerHeader_anyPlayer } from './__generated__/index.graphql.ts';

const StyledFullViewportScroll = styled(FullViewportScroll)`
  gap: var(--triple-unit);
`;

const Item = styled(LabelM)`
  display: flex;
  align-items: center;
  gap: var(--half-unit);
`;

const sportLabels = {
  [Sport.FOOTBALL]: 'Football',
  [Sport.BASEBALL]: 'MLB',
  [Sport.NBA]: 'NBA',
};

type Props = {
  player: AnyPlayerHeader_anyPlayer;
};

export const AnyPlayerHeader = ({ player }: Props) => {
  const { up: isTabletOrAbove } = useScreenSize('tablet');
  const { formatNumber, formatMessage, formatDate } = useIntlContext();
  const countries = useIntlCountries();
  const playersBreadcrumb = [
    {
      to: generateSportPath(ANY_SPORT_HOME, {
        sport: player.sport,
      }),
      name: sportLabels[player.sport],
    },
    {
      to: generateSportPath(ANY_SPORT_SCOUTING_CENTER, {
        sport: player.sport,
      }),
      name: formatMessage(glossary.players),
    },
    { name: player.displayName },
  ];

  const position =
    player.anyPositions?.[0] || ('position' in player && player.position);

  return (
    <>
      <NewHeader
        id={player.slug}
        breadcrumb={
          <Breadcrumb
            sections={playersBreadcrumb}
            gap={isTabletOrAbove ? 1.5 : 0.5}
          />
        }
        displayName={player.displayName}
        pictureUrl={player.squaredPictureUrl}
        color={player.activeClub?.styleGuideColor}
        details={
          <StyledFullViewportScroll>
            {!!(
              'rankingForPositionLeague' in player &&
              player.rankingForPositionLeague
            ) && (
              <Item color="var(--c-nd-600)">
                {!!player.activeClub?.domesticLeague && (
                  <LeagueAvatar
                    size={16}
                    league={player.activeClub.domesticLeague}
                  />
                )}
                <span>#{player.rankingForPositionLeague || 0}</span>
                <FormattedMessage {...positionShortNames[position]} />
              </Item>
            )}

            <Item color="var(--c-nd-600)">
              <FormattedNumber
                value={player.subscriptionsCount}
                maximumFractionDigits={-2}
              />
              <span>
                <FormattedMessage {...glossary.followers} />
              </span>
            </Item>
            <LastOwnedCardsByPlayer anyPlayer={player} withLabel />
          </StyledFullViewportScroll>
        }
        subscription={player}
        addToWatchlistNode={
          <AddToWatchlistButton
            anyPlayer={player}
            size="medium"
            color="quaternary"
            startIcon={<AddToWatchlistIcon />}
            blur
          />
        }
        metricsRow={<AnyPlayerHeaderRow anyPlayer={player} />}
      />
      <Helmet
        script={[
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'Person',
              name: player.displayName,
              givenName: player.firstName,
              familyName: player.lastName,
              ...(player.birthDay
                ? {
                    birthDate: formatDate(player.birthDay, {
                      timeZone: 'UTC',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    }),
                  }
                : {}),
              ...(player.height
                ? {
                    height: `${formatNumber(player.height / 1e2, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })} m`,
                  }
                : {}),
              nationality: countries.find(n => n.value === player?.country.code)
                ?.label,
              image: player.squaredPictureUrl,
              mainEntityOfPage: generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
                params: {
                  slug: player.slug,
                },
                sport: player.sport,
              }),
              affiliation: [
                ...(player.activeClub
                  ? [
                      {
                        '@type': 'SportsTeam',
                        name: player.activeClub.name,
                        mainEntityOfPage: generateSportPath(
                          ANY_SPORT_TEAMS_SLUG,
                          {
                            params: { slug: player.activeClub.slug },
                            sport: player.sport,
                          }
                        ),
                      },
                    ]
                  : []),
                ...(player.activeNationalTeam
                  ? [
                      {
                        '@type': 'SportsTeam',
                        name: player.activeNationalTeam.name,
                      },
                    ]
                  : []),
              ],
            }),
          },
        ]}
      />
    </>
  );
};

AnyPlayerHeader.fragments = {
  anyPlayer: gql`
    fragment AnyPlayerHeader_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      firstName
      lastName
      anyPositions
      squaredPictureUrl
      height
      subscriptionsCount
      activeClub {
        slug
        name
        styleGuideColor
        domesticLeague {
          slug
          name
          pictureUrl
        }
      }
      activeNationalTeam {
        slug
        name
      }
      country {
        slug
        code
        name
      }
      birthDay
      ... on Player {
        slug
        rankingForPositionLeague
        position
      }
      ...NewHeader_subscription
      ...LastOwnedCards_anyPlayer
      ...AnyPlayerHeaderRow_anyPlayer
      ...AddToWatchlistButton_anyPlayer
    }
    ${AddToWatchlistButton.fragments.anyPlayer}
    ${NewHeader.fragments.subscription}
    ${LastOwnedCardsByPlayer.fragments.anyPlayer}
    ${AnyPlayerHeaderRow.fragments.anyPlayer}
  ` as TypedDocumentNode<AnyPlayerHeader_anyPlayer>,
};
